import chevronRightIcon from "@layouts/betto/assets/chevronRight.svg";
import searchIcon from "@layouts/betto/assets/search.webp";
import { AuthCloseIcon } from "@layouts/betto/authPopup/icons/AuthCloseIcon.jsx";
import React, { useEffect } from "react";
import { Translate } from "react-localize-redux";

import {
	BettoGameGridPlaceholder,
	CARD_VARIANT,
} from "@pages/casino/containers/BettoGameGridTransition";
import { BettoExploreFilters } from "./BettoExploreFilters";
import { BettoExploreGameCard } from "./BettoExploreGameCard";
import {
	BettoExploreClearButton,
	BettoExploreCloseButton,
	BettoExploreEmpty,
	BettoExploreGrid,
	BettoExploreGridSection,
	BettoExploreHeader,
	BettoExploreOverlay,
	BettoExplorePageBox,
	BettoExplorePageCurrent,
	BettoExplorePageNav,
	BettoExplorePageNavIcon,
	BettoExplorePageTotal,
	BettoExplorePagination,
	BettoExplorePanel,
	BettoExploreRoot,
	BettoExploreSearchBar,
	BettoExploreSearchField,
	BettoExploreSearchIcon,
	BettoExploreSearchInput,
	BettoExploreSearchSection,
	BettoExploreTitle,
} from "./elements";
import useController from "./useController";

const BettoSearchModal = ({ onSearchToggle }) => {
	const {
		refs: { searchInputRef },
		state: {
			isToggled,
			searchValue,
			isSearchMode,
			isIdleState,
			selectedCategory,
			page,
			totalPages,
			games,
			categories,
			isLoadingGames,
			isFilterFetching,
		},
		handlers: {
			handleClose,
			handleSearchInput,
			handleClearInput,
			handleCategorySelect,
			handlePageChange,
		},
	} = useController({ onSearchToggle });

	useEffect(() => {
		if (isToggled) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}

		return () => {
			document.body.style.overflow = "unset";
		};
	}, [isToggled]);

	return (
		<Translate>
			{({ translate }) =>
				isToggled ? (
					<BettoExploreRoot $open>
						<BettoExploreOverlay
							type="button"
							aria-label="Close explore"
							onClick={handleClose}
						/>
						<BettoExplorePanel onClick={(event) => event.stopPropagation()}>
							<BettoExploreHeader>
								<BettoExploreTitle>
									{translate("betto.casino.explore")}
								</BettoExploreTitle>
								<BettoExploreCloseButton
									type="button"
									data-testid="button-search-modal-close"
									aria-label="Close"
									onClick={handleClose}>
									<AuthCloseIcon />
								</BettoExploreCloseButton>
							</BettoExploreHeader>

							<BettoExploreSearchSection>
								<BettoExploreSearchBar $focused={isSearchMode}>
									<BettoExploreSearchField>
										<BettoExploreSearchIcon
											src={searchIcon}
											alt=""
										/>
										<BettoExploreSearchInput
											data-testid="input-search-modal-search"
											innerRef={searchInputRef}
											placeholder={translate("betto.casino.searchGames")}
											onChange={handleSearchInput}
										/>
										{searchValue && (
											<BettoExploreClearButton
												type="button"
												data-testid="button-search-modal-clear"
												aria-label="Clear search"
												onClick={handleClearInput}>
												×
											</BettoExploreClearButton>
										)}
									</BettoExploreSearchField>
								</BettoExploreSearchBar>

								<BettoExploreFilters
									categories={categories}
									selectedCategory={selectedCategory}
									onCategorySelect={handleCategorySelect}
								/>
							</BettoExploreSearchSection>

							<BettoExploreGridSection>
								{isLoadingGames ? (
									<BettoGameGridPlaceholder
										cardVariant={CARD_VARIANT.GRID}
										GridComponent={BettoExploreGrid}
									/>
								) : games.length > 0 ? (
									<BettoExploreGrid className="search-game-container">
										{games.map((game, index) => (
											<BettoExploreGameCard
												key={game?.id || game?.game_id}
												game={game}
												imageIndex={index}
												onClose={handleClose}
											/>
										))}
									</BettoExploreGrid>
								) : isFilterFetching ? null : (
									<BettoExploreEmpty>
										{isSearchMode
											? translate("search.noMatch")
											: translate("search.infoText")}
									</BettoExploreEmpty>
								)}
							</BettoExploreGridSection>

							{!isIdleState && !isFilterFetching && totalPages > 1 && (
								<BettoExplorePagination>
									<BettoExplorePageNav
										type="button"
										aria-label="Previous page"
										disabled={page <= 1}
										onClick={() => handlePageChange(page - 1)}>
										<BettoExplorePageNavIcon
											src={chevronRightIcon}
											alt=""
											$mirrored
										/>
									</BettoExplorePageNav>

									<BettoExplorePageBox>
										<BettoExplorePageCurrent>{page}</BettoExplorePageCurrent>
										<BettoExplorePageTotal>
											{translate("betto.casino.of")} {totalPages}
										</BettoExplorePageTotal>
									</BettoExplorePageBox>

									<BettoExplorePageNav
										type="button"
										aria-label="Next page"
										disabled={page >= totalPages}
										onClick={() => handlePageChange(page + 1)}>
										<BettoExplorePageNavIcon
											src={chevronRightIcon}
											alt=""
										/>
									</BettoExplorePageNav>
								</BettoExplorePagination>
							)}
						</BettoExplorePanel>
					</BettoExploreRoot>
				) : null
			}
		</Translate>
	);
};

export default BettoSearchModal;
